// Pure helpers for the schedule History dialog. Kept out of the component so
// the labelling and ordering logic can be unit-tested without React.

export interface ScheduleSnapshot {
  id: string;
  briefing_id: string;
  created_at: string;
  created_by: string | null;
  actor_name: string | null;
  kind: string; // 'save' | 'pre_restore' | 'auto' | 'mobile_patch' (unknown → treated as 'save')
  block_count: number;
}

/** Short English label for a snapshot's kind (in-panel UI is English). */
export function snapshotKindLabel(kind: string): string {
  switch (kind) {
    case "auto":
      return "Autosave";
    case "pre_restore":
      return "Before restore";
    case "mobile_patch":
      return "Phone edit";
    case "save":
      return "Manual save";
    default:
      return "Manual save";
  }
}

/**
 * Whether a snapshot was published to the crew. Manual saves, phone edits
 * and the pre-restore safety copy reflect live (published) state; autosaves
 * are private recoverable drafts. Drives the History list's visual distinction.
 */
export function snapshotWasPublished(kind: string): boolean {
  return kind !== "auto";
}

/**
 * Human line for a snapshot row: locale-formatted timestamp + block count +
 * (optional) author. `locale` is injectable so tests are deterministic.
 */
export function describeSnapshot(
  snapshot: Pick<ScheduleSnapshot, "created_at" | "block_count" | "actor_name" | "kind">,
  locale?: string,
): string {
  const when = formatSnapshotTime(snapshot.created_at, locale);
  const blocks = `${snapshot.block_count} ${snapshot.block_count === 1 ? "block" : "blocks"}`;
  const who = snapshot.actor_name ? ` · ${snapshot.actor_name}` : "";
  return `${when} · ${blocks}${who}`;
}

/** Locale date-time, resilient to an unparseable timestamp. */
export function formatSnapshotTime(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Newest first. Snapshots are already fetched ordered, but the dialog sorts
 * defensively so a caller can pass an unsorted list.
 */
export function sortSnapshotsNewestFirst<T extends { created_at: string }>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}
